import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { IoMdSend } from 'react-icons/io';
import { RiRobot2Fill, RiUser3Fill } from "react-icons/ri";
import { VscFeedback } from "react-icons/vsc";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SourceAccordion from './SourceAccordion';
import AdminDashboard from './components/AdminDashboard';
import logo from './assets/W_01_KoTHEA_logo.png';

const ADMIN_PORT = '3001';

function App() {
  const isAdminMode = window.location.port === ADMIN_PORT;

  const [isAdmin, setIsAdmin] = useState(false);
  const [adminToken, setAdminToken] = useState('');
  // 인사말 문구는 백엔드(/api/theme)가 단일 소스. 여기서는 응답 전 fallback으로만 빈 값을 둔다.
  const [themeGreeting, setThemeGreeting] = useState('');
  const [themePrimaryColor, setThemePrimaryColor] = useState('#1A73E8');
  const [themeLogoUrl, setThemeLogoUrl] = useState(null);
  const [themeProfileUrl, setThemeProfileUrl] = useState(null);
  const [passwordInput, setPasswordInput] = useState('');

  // --- Alert & Confirm Modal State ---
  const [alertState, setAlertState] = useState({ isOpen: false, message: '' });
  const [confirmState, setConfirmState] = useState({ isOpen: false, message: '', onConfirm: null });
  const [feedbackModalState, setFeedbackModalState] = useState({ isOpen: false, query: '', answer: '', comment: '' });

  const showAlert = (message) => {
    setAlertState({ isOpen: true, message });
  };

  const closeAlert = () => {
    setAlertState({ isOpen: false, message: '' });
  };

  const showConfirm = (message, onConfirm) => {
    setConfirmState({ isOpen: true, message, onConfirm });
  };

  const closeConfirm = () => {
    setConfirmState({ isOpen: false, message: '', onConfirm: null });
  };

  const handleConfirmAction = () => {
    if (confirmState.onConfirm) {
      confirmState.onConfirm();
    }
    closeConfirm();
  };

  // 초기 메시지는 비워 두고, /api/theme 응답이 오면 인사말로 채운다.
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [educations, setEducations] = useState([]);
  const [selectedEducation, setSelectedEducation] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const applyPrimaryColor = (color) => {
    // CSS 인젝션 방지: hex 색상 코드만 허용
    if (!/^#[0-9a-fA-F]{3,8}$/.test(color)) return;
    let style = document.getElementById('theme-color-override');
    if (!style) {
      style = document.createElement('style');
      style.id = 'theme-color-override';
      document.head.appendChild(style);
    }
    style.textContent = `
      .bg-main-point { background-color: ${color} !important; }
      .text-main-point { color: ${color} !important; }
      .border-main-point { border-color: ${color} !important; }
      .hover\\:text-main-point:hover { color: ${color} !important; }
      .focus\\:border-main-point:focus { border-color: ${color} !important; }
    `;
  };

  useEffect(() => {
    fetch('/api/theme')
      .then(r => r.json())
      .then(theme => {
        if (theme.greeting) {
          setThemeGreeting(theme.greeting);
          setMessages([{ role: 'bot', content: theme.greeting }]);
        }
        if (theme.primary_color) {
          setThemePrimaryColor(theme.primary_color);
          applyPrimaryColor(theme.primary_color);
        }
        if (theme.has_custom_logo) {
          setThemeLogoUrl('/api/theme/logo?t=' + Date.now());
        }
        if (theme.has_custom_profile) {
          setThemeProfileUrl('/api/theme/profile?t=' + Date.now());
        }
      })
      .catch(() => {
        // 테마 로드 실패 시 빈 화면 대신 안내 문구를 노출한다.
        const fallback = '잠시 후 다시 시도해 주세요.';
        setThemeGreeting(fallback);
        setMessages([{ role: 'bot', content: fallback }]);
      });
  }, []);

  // 채팅 시작 전 선택할 교육 목록 로드 (활성 컬렉션 기준)
  useEffect(() => {
    fetch('/api/educations')
      .then(r => r.json())
      .then(data => setEducations(data.educations || []))
      .catch(() => {});
  }, []);

  // 교육 변경 시 이전 대화 맥락이 섞이지 않도록 대화를 초기화한다.
  const handleSelectEducation = (name) => {
    setSelectedEducation(name);
    if (!name) {
      // '변경' 클릭: 인사말로 초기화하여 교육 선택 카드를 다시 노출
      setMessages([{ role: 'bot', content: themeGreeting }]);
      return;
    }
    // 교육 선택: 선택 내용을 대화에 표시하고 채팅 시작 안내
    setMessages([
      { role: 'bot', content: themeGreeting },
      { role: 'user', content: name },
      { role: 'bot', content: `'${name}' 교육에 대한 채팅을 시작합니다. 궁금하신 점을 입력해 주세요.` },
    ]);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: passwordInput }),
      });
      if (res.ok) {
        const data = await res.json();
        setAdminToken(data.token);
        setIsAdmin(true);
        setPasswordInput('');
      } else {
        showAlert('비밀번호가 올바르지 않습니다.');
      }
    } catch {
      showAlert('서버 오류가 발생했습니다.');
    }
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;
    if (!selectedEducation) {
      showAlert('먼저 교육을 선택해 주세요.');
      return;
    }

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    setMessages(prev => [...prev, { role: 'bot', content: '', isLoading: true }]);

    try {
      // 싱글턴: 이전 대화는 전송하지 않고, 선택된 교육(category)만 함께 보낸다.
      const response = await axios.post('/api/chat', {
        query: input,
        category: selectedEducation
      });
      const { answer, sources } = response.data;

      setMessages(prev => {
        const newMessages = [...prev];
        newMessages[newMessages.length - 1] = { 
          role: 'bot', 
          content: answer, 
          sources: sources,
          isLoading: false 
        };
        return newMessages;
      });

    } catch (error) {
      console.error(error);
      setMessages(prev => {
        const newMessages = [...prev];
        newMessages[newMessages.length - 1] = { 
          role: 'bot', 
          content: '오류가 발생했습니다.', 
          isLoading: false 
        };
        return newMessages;
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.nativeEvent.isComposing) return; // 한글 조합 중 전송 방지

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFeedback = (query, answer) => {
    setFeedbackModalState({
      isOpen: true,
      query,
      answer,
      comment: ''
    });
  };

  const submitFeedback = async () => {
    try {
      await axios.post('/api/feedback', {
        query: feedbackModalState.query,
        answer: feedbackModalState.answer,
        comment: feedbackModalState.comment
      });
      setFeedbackModalState({ ...feedbackModalState, isOpen: false });
      showAlert("피드백이 전송되었습니다. 관리자가 확인 후 개선하겠습니다.");
    } catch (error) {
      console.error("Feedback error:", error);
      showAlert("피드백 전송 실패");
    }
  };

  return (
    <div className="flex flex-col h-screen bg-bg-main font-sans text-gray-800 relative">
      
      {/* 1. 상단 네비게이션 바 (Nav Bar) */}
      <nav className="w-full bg-main-point h-16 flex items-center px-6 sticky top-0 z-40 shadow-md">
        <img
          src={themeLogoUrl || logo}
          alt="KoTHEA Logo"
          className="h-10 w-auto object-contain brightness-0 invert"
        />
      </nav>

      {/* 2. 메인 컨텐츠 영역 (조건부 렌더링) */}
      {isAdminMode ? (
        isAdmin ? (
          <AdminDashboard token={adminToken} />
        ) : (
          <div className="flex-1 flex items-center justify-center bg-bg-main">
            <div className="bg-white rounded-xl p-8 w-full max-w-sm shadow-2xl">
              <h3 className="text-xl font-bold mb-6 text-gray-800 text-center">관리자 로그인</h3>
              <form onSubmit={handlePasswordSubmit}>
                <input
                  type="password"
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  placeholder="비밀번호를 입력하세요"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 mb-4 focus:outline-none focus:border-main-point text-sm"
                  autoFocus
                />
                <button
                  type="submit"
                  className="w-full px-4 py-3 bg-main-point text-white rounded-lg text-sm font-bold hover:bg-blue-700"
                >
                  로그인
                </button>
              </form>
            </div>
          </div>
        )
      ) : (
        <div className="flex-1 w-full max-w-4xl mx-auto flex flex-col relative overflow-hidden">
          
          <div className="flex-1 overflow-y-auto p-6 space-y-8 pb-40 scrollbar-hide">
            {messages.map((msg, index) => (
              <div key={index} className={`flex items-start gap-4 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                
                <div className={`rounded-full flex-shrink-0 overflow-hidden ${msg.role === 'user' ? 'bg-sub-element text-main-point p-2' : `bg-surface text-main-point shadow-sm border border-gray-100 ${themeProfileUrl ? '' : 'p-2'}`}`}>
                  {msg.role === 'user'
                    ? <RiUser3Fill size={24} />
                    : (themeProfileUrl
                        ? <img src={themeProfileUrl} alt="챗봇" className="w-10 h-10 rounded-full object-cover" />
                        : <RiRobot2Fill size={24} />)}
                </div>

                <div className={`rounded-[20px] px-6 py-5 max-w-[85%] shadow-card leading-7 text-[16px] font-medium transition-all duration-200 hover:shadow-lg
                  ${msg.role === 'user' 
                    ? 'bg-sub-element text-gray-900 rounded-tr-none' 
                    : 'bg-surface text-gray-900 rounded-tl-none border border-gray-50'
                  } ${msg.isLoading ? 'animate-pulse' : ''}`}>
                  
                  {msg.isLoading ? (
                     <div className="flex space-x-2 items-center h-6">
                        <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                        <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                        <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce"></div>
                     </div>
                  ) : (
                    <>
                      <div className="markdown text-gray-800 break-words">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {msg.content}
                        </ReactMarkdown>
                      </div>

                      {msg.role === 'bot' && msg.sources && (
                        <SourceAccordion sources={msg.sources} />
                      )}

                      {/* 피드백 버튼 추가 */}
                      {msg.role === 'bot' && !msg.isLoading && index > 0 && (
                        <div className="mt-2 flex justify-end">
                          <button
                            onClick={() => {
                              // 직전 메시지(사용자 질문) 찾기
                              const prevMsg = messages[index - 1];
                              if (prevMsg && prevMsg.role === 'user') {
                                handleFeedback(prevMsg.content, msg.content);
                              }
                            }}
                            className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1 transition-colors"
                            title="답변이 만족스럽지 않다면 클릭해주세요"
                          >
                            <VscFeedback /> 답변 피드백
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}

            {/* 교육 선택 카드 (선택 전): 대화 시작 시 봇 메시지 아래에 버튼으로 노출 */}
            {!selectedEducation && (
              <div className="flex items-start gap-4">
                <div className={`rounded-full flex-shrink-0 overflow-hidden bg-surface text-main-point shadow-sm border border-gray-100 ${themeProfileUrl ? '' : 'p-2'}`}>
                  {themeProfileUrl
                    ? <img src={themeProfileUrl} alt="챗봇" className="w-10 h-10 rounded-full object-cover" />
                    : <RiRobot2Fill size={24} />}
                </div>
                <div className="rounded-[20px] rounded-tl-none px-6 py-5 max-w-[85%] shadow-card bg-surface text-gray-900 border border-gray-50">
                  {educations.length > 0 ? (
                    <>
                      <p className="text-sm text-gray-500 mb-4">* 궁금하신 교육을 선택해 주세요.</p>
                      <div className="flex flex-col gap-2.5 min-w-[260px]">
                        {educations.map((edu) => (
                          <button
                            key={edu.name}
                            onClick={() => handleSelectEducation(edu.name)}
                            className="w-full py-3 px-4 border border-gray-300 rounded-xl text-center font-bold text-gray-700 hover:border-main-point hover:text-main-point hover:bg-blue-50 transition-colors"
                          >
                            {edu.name}
                          </button>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-gray-500">현재 선택 가능한 교육이 없습니다. 관리자에게 문의해 주세요.</p>
                  )}
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* 3. 하단 입력창 (화면 하단에 고정) */}
          <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-bg-main via-bg-main to-transparent pt-12">
            <div className="max-w-4xl mx-auto">
              <div className="bg-surface rounded-full shadow-card p-2 flex items-end transition-all focus-within:shadow-2xl focus-within:ring-1 focus-within:ring-blue-100">
                {/* 선택한 교육명 — 입력창 좌측에 표시. 칩을 클릭하면 교육 재선택 */}
                {selectedEducation && (
                  <button
                    type="button"
                    onClick={() => showConfirm('교육을 변경하시겠습니까?', () => handleSelectEducation(''))}
                    title="클릭하여 교육 변경"
                    className="flex-shrink-0 self-center ml-2 mr-1 px-3 py-1.5 max-w-[40%] truncate bg-sub-element text-main-point rounded-full text-sm font-medium transition-transform transform hover:scale-105 active:scale-95"
                  >
                    {selectedEducation}
                  </button>
                )}
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={!selectedEducation}
                  placeholder={selectedEducation ? "궁금한 내용을 입력하세요..." : "먼저 교육을 선택하세요"}
                  className="flex-1 bg-transparent outline-none px-6 py-3 resize-none max-h-32 text-gray-800 placeholder-gray-400 leading-6 disabled:cursor-not-allowed"
                  rows={1}
                  style={{ minHeight: '48px' }}
                  onInput={(e) => {
                    e.target.style.height = 'auto';
                    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
                  }}
                />
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || isLoading || !selectedEducation}
                  className={`p-3 rounded-full mb-1 mr-1 transition-all flex-shrink-0
                    ${!input.trim() || isLoading || !selectedEducation
                      ? 'bg-sub-element text-gray-400'
                      : 'bg-main-point text-white hover:bg-blue-700 shadow-md transform hover:scale-105 active:scale-95'
                    }`}
                >
                  <IoMdSend size={20} className={isLoading ? "animate-spin" : ""} />
                </button>
              </div>
              <div className="text-center mt-3 text-xs text-gray-400 font-light">
                 AI 챗봇은 실수할 수 있습니다. 중요한 정보는 확인이 필요합니다.
              </div>
            </div>
          </div>

        </div>
      )}

      {/* [추가] Alert Modal */}
      {alertState.isOpen && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-2xl">
            <h3 className="text-lg font-bold mb-4 text-gray-800">알림</h3>
            <p className="text-gray-600 mb-6">{alertState.message}</p>
            <div className="flex justify-end">
              <button 
                onClick={closeAlert}
                className="px-4 py-2 bg-main-point text-white rounded-lg text-sm font-bold hover:bg-blue-700"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* [추가] Confirm Modal */}
      {confirmState.isOpen && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-2xl">
            <h3 className="text-lg font-bold mb-4 text-gray-800">확인</h3>
            <p className="text-gray-600 mb-6">{confirmState.message}</p>
            <div className="flex justify-end gap-2">
              <button 
                onClick={closeConfirm}
                className="px-4 py-2 text-gray-500 hover:bg-gray-100 rounded-lg text-sm"
              >
                취소
              </button>
              <button 
                onClick={handleConfirmAction}
                className="px-4 py-2 bg-main-point text-white rounded-lg text-sm font-bold hover:bg-blue-700"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* [추가] Feedback Modal */}
      {feedbackModalState.isOpen && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-2xl">
            <h3 className="text-lg font-bold mb-4 text-gray-800">답변 피드백</h3>
            <p className="text-sm text-gray-600 mb-4">
              이 답변에 대해 피드백을 보내시겠습니까?
              <br />
              개선 의견을 남겨주시면 큰 도움이 됩니다.
            </p>
            <textarea
              value={feedbackModalState.comment}
              onChange={(e) => setFeedbackModalState({ ...feedbackModalState, comment: e.target.value })}
              placeholder="의견을 입력해주세요 (선택사항)"
              className="w-full border border-gray-300 rounded-lg px-4 py-2 mb-4 focus:outline-none focus:border-main-point h-24 resize-none text-sm"
            />
            <div className="flex justify-end gap-2">
              <button 
                onClick={() => setFeedbackModalState({ ...feedbackModalState, isOpen: false })}
                className="px-4 py-2 text-gray-500 hover:bg-gray-100 rounded-lg text-sm"
              >
                취소
              </button>
              <button 
                onClick={submitFeedback}
                className="px-4 py-2 bg-main-point text-white rounded-lg text-sm font-bold hover:bg-blue-700"
              >
                보내기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
